import React, {FC, SyntheticEvent, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {IntegrationFileAttachment} from '../../../../helpers/integrationHelpers';
import {CalendarProviderTypesEnum} from '../../../../models/api/calendarModel';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {AttachmentFileUploader} from '../../../common/attachments/attachmentsTypes';
import {useIntegrationAttachments} from '../../../inboxes/composer/toolbar/buttons/attachmentsHooks/attachmentsHook';
import {renderAttachmentButton} from './calendarAttachmentButton';
import {CalendarAttachmentsDropdownCoordinator} from './calendarAttachmentsDropdownCoordinator';

/*
 * Constants.
 */

enum CalendarAttachmentsTooltipsEnum {
  FRONTLINE_USER = 'frontline_user',
  ADMIN_WITHOUT_ANY_INTEGRATIONS = 'admin_without_any_integrations',
  ADMIN_WITHOUT_ALL_INTEGRATIONS = 'admin_without_all_integrations',
}

const frontlineUserTooltipText = (
  <Trans id="calendar-attachments-frontlineuser-tooltip">
    To attach files, Google Drive or Dropbox needs to be enabled for Front at your company. Please contact
    your administrator.
  </Trans>
);

const adminWithoutAnyIntegrationsTooltipText = (
  <Trans id="calendar-attachments-admin-withoutanyintegrations-tooltip">
    To attach files, Google Drive or Dropbox needs to be enabled for Front at your company. You can enable
    them in integrations.
  </Trans>
);

const adminWithoutAllIntegrationsTooltipText = (
  <Trans id="calendar-attachments-admin-withoutallintegrations-tooltip">
    Available options depend on whether Google Drive or Dropbox is enabled for Front at your company. You can
    enable them in integrations.
  </Trans>
);

/*
 * Props.
 */

export interface CalendarAttachmentsButtonCoordinatorPublicProps {
  onIntegrationAttachLinks: (arg: ReadonlyArray<IntegrationFileAttachment>) => void;
  onFileUpload: AttachmentFileUploader;
  provider: CalendarProviderTypesEnum;
}

interface CalendarAttachmentsButtonCoordinatorProps extends CalendarAttachmentsButtonCoordinatorPublicProps {
  integrations: ReadonlyArray<IntegrationModel>;
  isAdmin: boolean;
  navigateToLink: (arg: string) => void;
}

/*
 * Style.
 */

const StyledFileLabel = styled.label`
  display: none;
`;

const StyledCalendarAttachmentsWithTooltipDiv = styled.div`
  display: flex;
`;

const StyledCalendarAttachmentsTooltipDiv = styled.div`
  padding: 7px 0 0 5px;
`;

const StyledTooltipDiv = styled.div`
  padding: 10px;
  pointer-events: auto;
`;

const StyledTooltipButtonDiv = styled.div`
  width: 136px;

  padding-top: 20px;
  margin-left: auto;
  margin-right: 0;
`;

/*
 * Component.
 */

export const CalendarAttachmentsButtonCoordinator: FC<CalendarAttachmentsButtonCoordinatorProps> = (
  props,
) => {
  const {onIntegrationAttachLinks, onFileUpload} = props;

  const [inputKey, setInputKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Event handlers that open up the computer file selector.
  const onFileInputChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      // For each file in the input, add it.
      const {files} = event.currentTarget;
      const fileList = files && Array.from(files);
      if (fileList && fileList.length > 0) {
        fileList.forEach((f) => onFileUpload(f));
      }

      // Re-create the input to avoid existing-value issues.
      setInputKey((value) => value + 1);
    },
    [onFileUpload, setInputKey],
  );

  const onAttachFromComputerClick = useCallback(() => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();
  }, []);

  const {showIntegrationAttachmentsPicker} = useIntegrationAttachments(onIntegrationAttachLinks);
  const onIntegrationAttachClick = showIntegrationAttachmentsPicker;

  /*
   * Render.
   */

  const renderInputFilePicker = () => (
    <StyledFileLabel>
      <input ref={fileInputRef} key={inputKey} type="file" onChange={onFileInputChange} multiple />
    </StyledFileLabel>
  );

  return (
    <>
      {renderInputFilePicker()}
      {renderAttachmentsButtonCoordinator(props, onAttachFromComputerClick, onIntegrationAttachClick)}
    </>
  );
};

// The attachments button differs depending on the provider and which integrations are available.
function renderAttachmentsButtonCoordinator(
  props: CalendarAttachmentsButtonCoordinatorProps,
  onAttachFromComputerClick: () => void,
  onIntegrationAttachClick: (model: IntegrationModel) => void,
) {
  const {provider, navigateToLink, isAdmin, integrations} = props;

  switch (provider) {
    case CalendarProviderTypesEnum.OFFICE365:
      // The office graph API currently doesn't support remote attachments (`referenceAttachment`). When they do, we can add integrations.
      return renderAttachFromComputerButton(onAttachFromComputerClick);
    case CalendarProviderTypesEnum.GOOGLE:
      if (integrations.length === 0) {
        // When no integrations are available, show a disabled dropdown coordinator.
        return (
          <StyledCalendarAttachmentsWithTooltipDiv>
            <CalendarAttachmentsDropdownCoordinator
              integrations={integrations}
              onIntegrationAttachClick={onIntegrationAttachClick}
              isDisabled
            />
            {renderTooltipCoordinator(
              isAdmin
                ? CalendarAttachmentsTooltipsEnum.ADMIN_WITHOUT_ANY_INTEGRATIONS
                : CalendarAttachmentsTooltipsEnum.FRONTLINE_USER,
              navigateToLink,
            )}
          </StyledCalendarAttachmentsWithTooltipDiv>
        );
      }
      // When there's just one integration, directly show it as a button.
      if (integrations.length === 1) {
        return (
          <StyledCalendarAttachmentsWithTooltipDiv>
            {renderSingleIntegrationButton(integrations[0], onIntegrationAttachClick)}
            {isAdmin &&
              renderTooltipCoordinator(
                CalendarAttachmentsTooltipsEnum.ADMIN_WITHOUT_ALL_INTEGRATIONS,
                navigateToLink,
              )}
          </StyledCalendarAttachmentsWithTooltipDiv>
        );
      }
      // When there are multiple integrations, show the dropdown coordinator.
      return (
        <CalendarAttachmentsDropdownCoordinator
          integrations={integrations}
          onIntegrationAttachClick={onIntegrationAttachClick}
        />
      );
    default:
      reportToBugsnag(new Error('Rendering attachments button for event with unrecognized provider type.'));
      return null;
  }
}

function renderTooltipCoordinator(
  tooltipType: CalendarAttachmentsTooltipsEnum,
  navigateToLink: (arg: string) => void,
) {
  return (
    <StyledCalendarAttachmentsTooltipDiv>
      <TooltipCoordinator
        render={() => renderTooltip(tooltipType, navigateToLink)}
        placement="top"
        persistent
      />
    </StyledCalendarAttachmentsTooltipDiv>
  );
}

function renderTooltip(tooltipType: CalendarAttachmentsTooltipsEnum, navigateToLink: (arg: string) => void) {
  switch (tooltipType) {
    case CalendarAttachmentsTooltipsEnum.FRONTLINE_USER:
      return <StyledTooltipDiv>{frontlineUserTooltipText}</StyledTooltipDiv>;
    case CalendarAttachmentsTooltipsEnum.ADMIN_WITHOUT_ANY_INTEGRATIONS:
      return (
        <StyledTooltipDiv>
          {adminWithoutAnyIntegrationsTooltipText}
          {renderTooltipButton(navigateToLink)}
        </StyledTooltipDiv>
      );
    case CalendarAttachmentsTooltipsEnum.ADMIN_WITHOUT_ALL_INTEGRATIONS:
      return (
        <StyledTooltipDiv>
          {adminWithoutAllIntegrationsTooltipText}
          {renderTooltipButton(navigateToLink)}
        </StyledTooltipDiv>
      );
    default:
      return null;
  }
}

function renderTooltipButton(navigateToLink: (arg: string) => void) {
  return (
    <StyledTooltipButtonDiv>
      <Button
        size={VisualSizesEnum.SMALL}
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={() => navigateToLink(`/settings/integrations/native`)}
      >
        <Trans id="calendar-attachments-button-go-to-integrations">Go to integrations</Trans>
      </Button>
    </StyledTooltipButtonDiv>
  );
}

function renderAttachFromComputerButton(onAttachFromComputerClick: () => void) {
  return renderAttachmentButton({
    onClick: onAttachFromComputerClick,
    buttonText: <Trans id="calendar-attachments-button-attach-from-computer">Attach from computer</Trans>,
    shouldShowChevron: false,
  });
}

function renderSingleIntegrationButton(
  integration: IntegrationModel,
  onIntegrationAttachClick: (model: IntegrationModel) => void,
) {
  const integrationButtonText = (
    <Trans id="calendar-attachments-button-attach-with-integration">Attach with {integration.name}</Trans>
  );
  return renderAttachmentButton({
    onClick: () => onIntegrationAttachClick(integration),
    buttonText: integrationButtonText,
    shouldShowChevron: false,
  });
}
